import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["inquiry", "confirmed", "in_progress", "completed", "cancelled"];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [b, t] = await Promise.all([api.get("/bookings"), api.get("/team")]);
    setBookings(b.data);
    setTeam(t.data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = bookings.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    const q = search.trim().toLowerCase();
    if (q && !`${b.client_name} ${b.client_email} ${b.client_phone} ${b.event_type}`.toLowerCase().includes(q)) return false;
    const assigned = b.assigned_to_ids || (b.assigned_to ? [b.assigned_to] : []);
    if (teamFilter !== "all" && !assigned.includes(teamFilter)) return false;
    const total = Number(b.total_amount || 0), paid = Number(b.advance_paid || 0);
    const payState = paid >= total && total > 0 ? "paid" : paid > 0 ? "partial" : "due";
    if (paymentFilter !== "all" && payState !== paymentFilter) return false;
    if (dateFrom && b.event_date < dateFrom) return false;
    if (dateTo && b.event_date > dateTo) return false;
    return true;
  });

  const updateBooking = async (id, patch) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, patch);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...data } : b)));
      if (selected?.id === id) setSelected({ ...selected, ...data });
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSelected(null);
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-bookings" className="p-4 md:p-8 2xl:p-10 max-w-[1900px] mx-auto">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Client Management</div>
          <h1 className="font-serif-display text-4xl">Bookings</h1>
        </div>
        <div className="text-sm text-neutral-500">{filtered.length} result(s)</div>
      </div>

      <div className="bg-[#0a0a0c] border border-white/10 p-4 mb-4 grid md:grid-cols-2 xl:grid-cols-6 gap-3">
        <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search client / event..." className="line-input py-3 px-3" />
        <select value={filter} onChange={(e)=>setFilter(e.target.value)} className="line-input py-3"><option value="all">All statuses</option>{STATUSES.map(s=><option key={s} value={s}>{s}</option>)}</select>
        <select value={teamFilter} onChange={(e)=>setTeamFilter(e.target.value)} className="line-input py-3"><option value="all">All team members</option>{team.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select>
        <select value={paymentFilter} onChange={(e)=>setPaymentFilter(e.target.value)} className="line-input py-3"><option value="all">All payments</option><option value="paid">Paid</option><option value="partial">Partial</option><option value="due">Due</option></select>
        <input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} className="line-input py-3" title="From date" />
        <input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} className="line-input py-3" title="To date" />
      </div>

      <div className="bg-[#0a0a0c] border border-white/10 rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 overline text-neutral-500">
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Event</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Assigned</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-1">Status</div>
        </div>
        {loading ? (
          <div className="p-6 text-neutral-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-neutral-500 text-sm">No bookings.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((b) => (
              <button
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                onClick={() => setSelected(b)}
                className="w-full text-left grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-white/5 transition-colors"
              >
                <div className="col-span-3">
                  <div className="text-white">{b.client_name}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {b.client_email}
                  </div>
                </div>
                <div className="col-span-2 text-sm text-neutral-300">
                  {b.event_type}
                </div>
                <div className="col-span-2 text-sm text-neutral-400">
                  {b.event_date}
                  {b.event_time && (
                    <span className="text-neutral-500"> · {b.event_time}</span>
                  )}
                </div>
                <div className="col-span-2 text-sm text-neutral-400">
                  {(b.assigned_names || []).join(", ") || b.assigned_name || "—"}
                </div>
                <div className="col-span-2 text-sm">
                  <span className="text-white">₹{b.total_amount || 0}</span>
                  <span className="text-neutral-500">
                    {" / "}
                    ₹{b.advance_paid || 0} paid
                  </span>
                </div>
                <div className="col-span-1">
                  <span className="text-xs uppercase tracking-widest text-neutral-400">
                    {b.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <BookingDrawer
          booking={selected}
          team={team}
          onClose={() => setSelected(null)}
          onUpdate={updateBooking}
          onDelete={deleteBooking}
        />
      )}
    </div>
  );
}

function BookingDrawer({ booking, team, onClose, onUpdate, onDelete }) {
  const [form, setForm] = useState({
    status: booking.status,
    assigned_to_ids: booking.assigned_to_ids || (booking.assigned_to ? [booking.assigned_to] : []),
    total_amount: booking.total_amount || 0,
    advance_paid: booking.advance_paid || 0,
    notes: booking.notes || "",
    task_name: booking.task_name || booking.event_type || "",
    internal_note: booking.internal_note || booking.notes || "",
    event_date: booking.event_date || "",
    event_time: booking.event_time || "",
    location: booking.location || "",
  });

  useEffect(() => {
    setForm({
      status: booking.status,
      assigned_to_ids: booking.assigned_to_ids || (booking.assigned_to ? [booking.assigned_to] : []),
      total_amount: booking.total_amount || 0,
      advance_paid: booking.advance_paid || 0,
      notes: booking.notes || "",
      event_date: booking.event_date || "",
      event_time: booking.event_time || "",
      location: booking.location || "",
    });
  }, [booking]);

  const save = async () => {
    await onUpdate(booking.id, {
      ...form,
      total_amount: Number(form.total_amount),
      advance_paid: Number(form.advance_paid),
      assigned_to_ids: form.assigned_to_ids || [],
      assigned_to: null,
    });
  };

  const due = (Number(form.total_amount) || 0) - (Number(form.advance_paid) || 0);

  return (
    <div
      data-testid="booking-drawer"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#0a0a0c] border-l border-white/10 h-full overflow-y-auto p-8"
      >
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="overline text-neutral-500">Booking</div>
            <h2 className="font-serif-display text-3xl mt-2">
              {booking.client_name}
            </h2>
            <div className="text-sm text-neutral-400 mt-1">
              {booking.client_email} · {booking.client_phone}
            </div>
          </div>
          <button
            data-testid="drawer-close"
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-sm uppercase tracking-widest"
          >
            Close
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="overline block mb-2">Status</label>
            <select
              data-testid="drawer-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="line-input w-full py-3"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="bg-[#0a0a0c]">
                  {s}
                </option>
              ))}
            </select>
          </div>

          <TeamMultiSelect
            team={team}
            value={form.assigned_to_ids || []}
            onChange={(ids) => setForm({ ...form, assigned_to_ids: ids })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="overline block mb-2">Task name</label><input value={form.task_name || ""} onChange={(e)=>setForm({...form,task_name:e.target.value})} placeholder="e.g. Wedding photography" className="line-input w-full py-3" /></div>
            <div><label className="overline block mb-2">Internal note</label><textarea value={form.internal_note || ""} onChange={(e)=>setForm({...form,internal_note:e.target.value})} placeholder="Visible to team/admin only" className="line-input w-full py-3 min-h-[90px]" /></div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Total amount</label>
              <input
                type="number"
                data-testid="drawer-total"
                value={form.total_amount}
                onChange={(e) =>
                  setForm({ ...form, total_amount: e.target.value })
                }
                className="line-input w-full py-3"
              />
            </div>
            <div>
              <label className="overline block mb-2">Advance paid</label>
              <input
                type="number"
                data-testid="drawer-advance"
                value={form.advance_paid}
                onChange={(e) =>
                  setForm({ ...form, advance_paid: e.target.value })
                }
                className="line-input w-full py-3"
              />
            </div>
          </div>

          <div className="border border-white/10 p-4 rounded-sm flex justify-between">
            <span className="overline text-neutral-500">Due amount</span>
            <span className="font-serif-display text-2xl text-[#fbbf24]">
              ₹{due.toLocaleString()}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Event date</label>
              <input
                type="date"
                data-testid="drawer-date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="line-input w-full py-3 [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Event time</label>
              <input
                type="time"
                data-testid="drawer-time"
                value={form.event_time}
                onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                className="line-input w-full py-3 [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Location</label>
            <input
              data-testid="drawer-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="line-input w-full py-3"
            />
          </div>

          <div>
            <label className="overline block mb-2">Client message</label>
            <div className="text-sm text-neutral-400 p-4 border border-white/10 rounded-sm">
              {booking.message || "—"}
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Internal notes</label>
            <textarea
              rows={3}
              data-testid="drawer-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="line-input w-full py-3 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              data-testid="drawer-save"
              onClick={save}
              className="flex-1 bg-white text-black py-3 text-sm uppercase tracking-[0.2em] hover:bg-neutral-200 transition-colors"
            >
              Save changes
            </button>
            <button
              data-testid="drawer-delete"
              onClick={() => onDelete(booking.id)}
              className="px-6 py-3 text-sm uppercase tracking-[0.2em] border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function TeamMultiSelect({ team, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const filtered = useMemo(() => team.filter(t => `${t.name} ${t.email || ""} ${t.work_role || t.role || ""}`.toLowerCase().includes(query.toLowerCase())), [team, query]);
  const names = team.filter(t => value.includes(t.id)).map(t => t.name);
  const toggle = (id) => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  return <div ref={boxRef} className="relative">
    <label className="overline block mb-2">Assign team members</label>
    <button type="button" onClick={() => setOpen(v => !v)} className="line-input w-full py-3 px-3 text-left flex justify-between items-center">
      <span className={names.length ? "text-white" : "text-neutral-500"}>{names.length ? names.join(", ") : "Select team members"}</span>
      <span>⌄</span>
    </button>
    {open && <div className="absolute z-30 mt-2 w-full bg-[#0a0a0c] border border-white/15 shadow-2xl">
      <div className="p-3 border-b border-white/10"><input autoFocus value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search name, email or role..." className="line-input w-full py-2 px-3" /></div>
      <div className="max-h-60 overflow-y-auto divide-y divide-white/5">{filtered.length ? filtered.map(t => <label key={t.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5"><input type="checkbox" checked={value.includes(t.id)} onChange={()=>toggle(t.id)} /><span className="flex-1">{t.name}</span><span className="text-neutral-500 text-xs">{t.work_role || t.role}</span></label>) : <div className="p-4 text-sm text-neutral-500">No team member found.</div>}</div>
      <div className="p-3 border-t border-white/10 flex justify-between text-xs text-neutral-400"><span>{value.length} selected</span><button type="button" onClick={()=>setOpen(false)} className="text-white uppercase tracking-wider">Done</button></div>
    </div>}
  </div>;
}
